import { AUTH_HEADERS } from '@trip-ux/constants';
import { ValidationErrors } from 'final-form';
import {
  DateRangeValidationMessages,
  hasValidDateRange,
  resolveValidationLabel,
  validateReportDateRange
} from '../utils/reportValidation';

interface AdvisorReportValidationMessages extends DateRangeValidationMessages {
  mgaOrganizationCodeRequired: string;
  advisorCodeRequired: string;
  reportNameRequired: string;
  reportMonthRequired: string;
  reportYearRequired: string;
  advisorCodeInvalid: string;
  mgaOrganizationCodeInvalid: string;
}

const getValidationMessages = (labels?: unknown): AdvisorReportValidationMessages => ({
  mgaOrganizationCodeRequired: resolveValidationLabel(labels, ['formErrors.mgaOrganizationCodeRequired', 'validationErrors.mgaOrganizationCodeRequired'],'Please select MGA name/code'),
  advisorCodeRequired: resolveValidationLabel(labels, ['formErrors.advisorCodeRequired', 'validationErrors.advisorCodeRequired'], 'Please select Advisor name/code'),
  reportNameRequired: resolveValidationLabel(labels, ['formErrors.reportNameRequired', 'validationErrors.reportNameRequired'],'Please select report name'),
  reportMonthRequired: resolveValidationLabel(labels, ['formErrors.reportMonthRequired', 'validationErrors.reportMonthRequired'], 'Please select report month'),
  reportYearRequired: resolveValidationLabel(labels, ['formErrors.reportYearRequired', 'validationErrors.reportYearRequired'], 'Please select report year'),
  advisorCodeInvalid: resolveValidationLabel(labels, ['formErrors.advisorCodeInvalid', 'validationErrors.advisorCodeInvalid'],'Please enter valid advisor name/code'),
  mgaOrganizationCodeInvalid: resolveValidationLabel(labels, ['formErrors.mgaOrganizationCodeInvalid', 'validationErrors.mgaOrganizationCodeInvalid'],'Please enter valid MGA name/code'),
  reportEndDateRequired: resolveValidationLabel(labels, ['formErrors.reportEndDateRequired', 'validationErrors.reportEndDateRequired'], 'Please select an end date'),
  reportStartDateRequired: resolveValidationLabel(labels, ['formErrors.reportStartDateRequired', 'validationErrors.reportStartDateRequired'], 'Please select a start date'),
  startDateFuture: resolveValidationLabel(labels, ['formErrors.startDateFuture', 'validationErrors.startDateFuture'], 'Start date cannot be in the future'),
  endDateFuture: resolveValidationLabel(labels, ['formErrors.endDateFuture', 'validationErrors.endDateFuture'], 'End date cannot be in the future'),
  startDateAfterEndDate: resolveValidationLabel(labels, ['formErrors.startDateAfterEndDate', 'validationErrors.startDateAfterEndDate'], 'Start date cannot be after end date'),
  dateRangeExceeded365Days: resolveValidationLabel(labels, ['formErrors.dateRangeExceeded365Days', 'validationErrors.dateRangeExceeded365Days'], 'Date range cannot exceed 365 days')
});

const requireFields = (value, userType, suggestedAgents, organizationDropDownList, messages: AdvisorReportValidationMessages): ValidationErrors => {
  const errors: ValidationErrors = {};
  const isFieldRequired = (field, condition, errorMsg) => {
    if (condition) {
      errors[field] = errorMsg;
    }
  };

  const checkOrganizationCodeRequired = () => {
    if (userType !== AUTH_HEADERS.CIAM_ID) {
      isFieldRequired('mgaOrganizationCode', !value.mgaOrganizationCode, messages.mgaOrganizationCodeRequired);
    }
  };

  const checkAdvisorCodeRequired = () => {
    isFieldRequired('advisorCode', !value.advisorCode, messages.advisorCodeRequired);
  };
  const checkReportNameRequired = () => {
    isFieldRequired('reportName', !value.reportName, messages.reportNameRequired);
  };
  const checkReportMonthRequired = () => {
    isFieldRequired('reportMonth', !value.reportMonth, messages.reportMonthRequired);
  };
  const checkReportYearRequired = () => {
    isFieldRequired('reportYear', !value.reportYear, messages.reportYearRequired);
  };

  const validateAgentNameError = () => {
    const agentError = validateAgentName(value.advisorCode, suggestedAgents, messages);
    if (agentError) {
      errors.advisorCode = agentError;
    }
  };

  const validateOrganizationCodeError = () => {
    if (userType !== AUTH_HEADERS.CIAM_ID) {
      const organizationError = validateOrganizationCode(value.mgaOrganizationCode, organizationDropDownList, messages);
      if (organizationError) {
        errors.mgaOrganizationCode = organizationError;
      }
    }
  };

  // Run all checks
  checkOrganizationCodeRequired();
  checkAdvisorCodeRequired();
  validateAgentNameError();
  validateOrganizationCodeError();
  checkReportNameRequired();

  // Month and year are only required when no date range is provided
  const hasDateRange = hasValidDateRange(value);
  if (!hasDateRange) {
    checkReportMonthRequired();
    checkReportYearRequired();
  }

  return errors;
};

const validateAgentName = (value, suggestedAgents, messages: AdvisorReportValidationMessages) => {
  if (!value) {
    return undefined;
  }

  const isValidAgent = suggestedAgents.some(agent => agent.label === value || agent.value === value);
  return isValidAgent ? undefined : messages.advisorCodeInvalid;
};

const validateOrganizationCode = (value, organizationDropDownList, messages: AdvisorReportValidationMessages) => {
  if (!value) {
    return undefined;
  }

  const isValid = organizationDropDownList.some(org => org.value === value.toString());
  return isValid ? undefined : messages.mgaOrganizationCodeInvalid;
};

export default function (value, userType, suggestedAgents, organizationDropDownList, labels?: unknown): ValidationErrors {
  const errors: { [prop: string]: unknown } = {};
  const messages = getValidationMessages(labels);

  return {
    ...requireFields(value, userType, suggestedAgents, organizationDropDownList, messages),
    ...validateReportDateRange(value, messages),
    ...errors
  };
}
